import {
    SYSTEM_PROGRAM_ID_STRING,
    TOKEN_2022_PROGRAM_ID_STRING,
    TOKEN_PROGRAM_ID_STRING
} from "@solomon-labs/constants";
import { useCallback, useEffect, useState } from "react";

import { useSimMode } from "../sim/SimModeContext";
import { LEGACY_USDV_MINT, USDV_MINT } from "../tokens/usdv";
import { type SolanaAccountSummary, SolanaAddress } from "../utils/address";

// Debounce window before firing the Tier-3 on-chain lookup. Sub-300ms
// fires on every typing pause; >500ms feels laggy after a paste. 400ms
// is the sweet spot: long enough that a single keystroke doesn't spawn
// an RPC, short enough that the user sees "Verifying…" within a beat
// of stopping typing.
const DEFAULT_DEBOUNCE_MS = 400;

/** Auto-retry schedule on `failed`. After every retry attempt fails,
 *  the next delay steps up; after `RETRY_DELAYS.length` attempts, we
 *  give up and surface "please try again later." Address-change
 *  resets the counter back to 0. */
const RETRY_DELAYS_SEC = [5, 10] as const;

/**
 * Lifecycle of the on-chain verification:
 *  - `idle`     — initial / empty / failed local validation. No RPC.
 *  - `checking` — post-debounce, RPC in-flight.
 *  - `new`      — RPC returned, no account at this address yet.
 *  - `found`    — RPC returned an existing account.
 *  - `failed`   — RPC errored. Local Tier-1+2 still gates the field
 *                 visually; this state is a soft signal only.
 */
export type SolanaAddressVerificationStatus = "idle" | "checking" | "new" | "found" | "failed";

/** UI tone for the verification status line — maps 1:1 to a brand
 *  color slot at the call site. `error` is reserved for
 *  hard-invalid cases (paired with `invalid: true`) — most
 *  surfaces should also flip the field's border + disable submit
 *  on that tone. */
export type SolanaAddressVerificationTone = "neutral" | "success" | "warning" | "muted" | "error";

/**
 * Tone → foreground utility for rendering `verification.text`.
 *
 * Lives here rather than at each call site: four surfaces were carrying
 * byte-identical ternary ladders (the address-book form, the inline
 * destination editor, the add-destination form, and
 * RewardsDestinationField's own status row), so a tone added to the
 * union above had four places to forget.
 */
export const VERIFICATION_TONE_CLASS: Record<SolanaAddressVerificationTone, string> = {
    error: "text-error-base",
    success: "text-success-base",
    warning: "text-warning-base",
    muted: "text-foreground-imprint",
    neutral: "text-foreground-neutral"
};

/** Account-type taxonomy used by both the verification hook and the
 *  `AccountTypeBadge` component that visualizes it. Lives in shared
 *  so the badge component (in `apps/main`) and the hook stay in
 *  lockstep — adding a category here keeps the mapping table in one
 *  place. */
export type AccountType = "wallet" | "token" | "program" | "new" | "unknown";

export interface SolanaAddressVerification {
    status: SolanaAddressVerificationStatus;
    /** Account info from the RPC, when `status === "found"`. Null
     *  in every other state. */
    account: SolanaAccountSummary | null;
    /** UI-ready copy for the status line, or `null` when there's
     *  nothing to surface (e.g. the address hasn't passed local
     *  validation yet). Branches:
     *    - "Verifying account..."          when checking
     *    - "This account is empty"         when no account exists
     *    - "Valid account"                 when owner = System Program + on-curve
     *    - "Is this address correct?"      when owner = SPL Token / Token-2022 /
     *                                        any program (incl. off-curve
     *                                        System-owned). Paired with
     *                                        `accountType` so callers can
     *                                        render an `AccountTypeBadge`
     *                                        inline with the text.
     *    - "Couldn't verify on chain"      when the RPC failed
     */
    text: string | null;
    /** Tone for the status copy. Callers map this to a Tailwind
     *  color class (the hook stays Tailwind-agnostic). */
    tone: SolanaAddressVerificationTone;
    /** When non-null, the caller should render an `AccountTypeBadge`
     *  with this type before the status text. Set for every
     *  post-RPC classification (`wallet`/`token`/`program`/`new`),
     *  null while we're still resolving (idle/checking/failed). */
    accountType: AccountType | null;
    /** `true` for hard-invalid classifications — the address will
     *  *literally fail* to receive USDv on chain:
     *    - It's a token mint (mints can't receive transfers).
     *    - It's a token account whose mint isn't USDv (would fail
     *      with a mint-mismatch error on submit).
     *  Callers should treat this as form-level invalid: flip the
     *  field's border to `error` and disable the submit CTA. Set
     *  independently of `tone` so callers can decide whether the
     *  text styling tracks invalidity or stays soft. */
    invalid: boolean;
    /** Manually re-run the Tier 3 RPC. Useful when `status === "failed"`
     *  so the user can retry after a transient network error without
     *  changing the input. No-op if the address doesn't currently
     *  pass the local check. */
    retry: () => void;
}

interface UseSolanaAddressVerificationOptions {
    /** Override the debounce window before the RPC fires. */
    debounceMs?: number;
    /** Set false to suppress the RPC even when the address is
     *  locally valid (e.g. while the form is submitting). Defaults
     *  to true. */
    enabled?: boolean;
    /**
     * Permit a legacy USDv token account only in an explicit migration
     * context. Normal product destinations must use current Token-2022 USDv.
     */
    allowLegacyUsdvTokenAccount?: boolean;
}

/**
 * Check whether a token account can receive USDv in this verification
 * context. Legacy USDv stays migration-only and is never accepted by default.
 */
export function isAllowedUsdvTokenAccountMint(mint: string | undefined, allowLegacyUsdvTokenAccount = false): boolean {
    return mint === USDV_MINT || (allowLegacyUsdvTokenAccount && mint === LEGACY_USDV_MINT);
}

function isAllowedUsdvTokenAccount(
    mint: string | undefined,
    tokenProgram: string,
    allowLegacyUsdvTokenAccount: boolean
): boolean {
    return (
        (mint === USDV_MINT && tokenProgram === TOKEN_2022_PROGRAM_ID_STRING) ||
        (allowLegacyUsdvTokenAccount && mint === LEGACY_USDV_MINT && tokenProgram === TOKEN_PROGRAM_ID_STRING)
    );
}

/**
 * Debounced on-chain verification for a user-entered Solana address.
 *
 * Local Tier-1 (`SolanaAddress.isShape`) gates the RPC — invalid
 * input never fires a request, but valid PDAs (off-curve 32-byte
 * addresses like Squads multisig vaults) pass through so the
 * on-chain check can classify them. Once the user settles for
 * `debounceMs`, `SolanaAddress.fetchAccount` runs and the result
 * classifies the account by `owner`:
 *
 *   - No account on chain      → `status: "new"`, neutral tone
 *   - Owner = System Program   → `status: "found"`, success tone
 *   - Owner = SPL Token / 2022 → `status: "found"`, warning tone
 *   - Owner = other program    → `status: "found"`, warning tone (PDA)
 *
 * A late response from a superseded keystroke is dropped via a
 * closure flag — no AbortController because `fetchAccount` doesn't
 * currently thread a signal through ConnectionManager.
 */
export function useSolanaAddressVerification(
    address: string,
    options: UseSolanaAddressVerificationOptions = {}
): SolanaAddressVerification {
    const { debounceMs = DEFAULT_DEBOUNCE_MS, enabled = true, allowLegacyUsdvTokenAccount = false } = options;
    // Sim mode short-circuits the Tier-3 RPC entirely so the exported
    // widgets (Set Reward Destination, Enable Rewards) verify addresses
    // fully offline — no `fetchAccount`, no network. Resolved at the
    // bottom of the hook; the effect below also skips the RPC when on.
    const sim = useSimMode();
    const trimmed = (address ?? "").trim();
    // Tier-1 only (`isShape`) — we want PDAs to flow through to the
    // RPC so the result can classify them as `program` / `token` /
    // etc. Tier-2 (`isWallet` / on-curve) would block PDAs at the
    // gate, which is too strict for destinations like multisig
    // vaults or DAO treasuries.
    const localValid = SolanaAddress.isShape(trimmed);
    const [state, setState] = useState<{
        status: SolanaAddressVerificationStatus;
        account: SolanaAccountSummary | null;
    }>({ status: "idle", account: null });
    // Manual-retry trigger. Incremented by `retry()` so the fetching
    // effect re-fires without needing the address itself to change.
    const [retryNonce, setRetryNonce] = useState(0);
    // Auto-retry counter — how many *automatic* retries we've fired
    // for the current address. 0 = original attempt, 1 = first
    // retry, 2 = retries exhausted (show "please try again later").
    const [retryAttempt, setRetryAttempt] = useState(0);
    // Seconds remaining on the countdown until the next auto-retry
    // fires. 0 when no countdown is active. Falls back to the
    // initial delay in the display layer so the "retrying in N..."
    // text never momentarily shows 0.
    const [countdown, setCountdown] = useState(0);

    // Reset the retry budget whenever the address changes — fresh
    // address gets a fresh 5s/10s schedule.
    useEffect(() => {
        setRetryAttempt(0);
        setCountdown(0);
    }, [trimmed]);

    // Auto-retry on failure: when status flips to "failed" and we
    // haven't exhausted retries, kick off a 1-second-tick countdown.
    // At 0, increment the attempt counter + retryNonce — the
    // fetching effect re-fires for the same address.
    useEffect(() => {
        if (state.status !== "failed") return;
        if (retryAttempt >= RETRY_DELAYS_SEC.length) return;
        const delaySeconds = RETRY_DELAYS_SEC[retryAttempt];
        setCountdown(delaySeconds);
        const intervalId = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(intervalId);
                    setRetryAttempt((n) => n + 1);
                    setRetryNonce((n) => n + 1);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return (): void => clearInterval(intervalId);
    }, [state.status, retryAttempt]);

    useEffect(() => {
        // Sim mode resolves synthetically at the return below — never
        // schedule the RPC.
        if (sim.enabled) return;
        if (!enabled || !trimmed || !localValid) {
            setState({ status: "idle", account: null });
            return;
        }
        setState({ status: "checking", account: null });
        let cancelled = false;
        const handle = setTimeout(async () => {
            try {
                const account = await SolanaAddress.fetchAccount(trimmed);
                if (cancelled) return;
                setState({
                    status: account ? "found" : "new",
                    account
                });
            } catch {
                if (cancelled) return;
                setState({ status: "failed", account: null });
            }
        }, debounceMs);
        return (): void => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [trimmed, localValid, enabled, debounceMs, retryNonce, sim.enabled]);

    const retry = useCallback(() => {
        setRetryNonce((n) => n + 1);
    }, []);

    // Sim-mode resolution — no RPC. A shape-valid Solana address reads as a
    // found single-key wallet ("Valid account" + Wallet badge); empty /
    // shape-invalid / disabled stays idle. Mirrors the happy-path
    // `deriveCopy` output for a System-owned, on-curve account without
    // relying on the address actually being on-curve (sim addresses are
    // arbitrary). This is what lets the sim export run fully offline.
    if (sim.enabled) {
        const valid = enabled && trimmed.length > 0 && localValid;
        return {
            status: valid ? "found" : "idle",
            account: null,
            text: valid ? "Valid account" : null,
            tone: valid ? "success" : "neutral",
            accountType: valid ? "wallet" : null,
            invalid: false,
            retry
        };
    }

    return {
        status: state.status,
        account: state.account,
        retry,
        ...deriveCopy(
            state.status,
            state.account,
            trimmed,
            {
                attempt: retryAttempt,
                countdown
            },
            allowLegacyUsdvTokenAccount
        )
    };
}

/** Map `(status, account, address)` to the UI copy + tone +
 *  accountType. Pulled out of the hook body so the switch is easy to
 *  extend with new owner classifications later. The address itself
 *  is passed in so the System-owner branch can split wallet
 *  (on-curve) from off-curve program-controlled accounts without a
 *  second RPC. */
function deriveCopy(
    status: SolanaAddressVerificationStatus,
    account: SolanaAccountSummary | null,
    address: string,
    retryState: { attempt: number; countdown: number },
    allowLegacyUsdvTokenAccount: boolean
): {
    text: string | null;
    tone: SolanaAddressVerificationTone;
    accountType: AccountType | null;
    invalid: boolean;
} {
    switch (status) {
        case "checking":
            return {
                text: "Verifying account...",
                tone: "muted",
                accountType: null,
                invalid: false
            };
        case "new":
            return {
                text: "This account is empty",
                tone: "neutral",
                accountType: "new",
                invalid: false
            };
        case "found": {
            const owner = account?.owner;
            // System Program + on-curve → single-key wallet, the
            // happy path. System Program + off-curve = an off-curve
            // System-owned account; semantically program-controlled
            // (something other than a single keypair gates spending),
            // so it falls through to the Program branch below.
            if (owner === SYSTEM_PROGRAM_ID_STRING && SolanaAddress.isOnCurve(address)) {
                // TODO: Sanctioned-address check. When a sanctions-
                // list integration lands (Circle's USDC blocklist /
                // Chainalysis / OFAC SDN / Solomon-maintained list),
                // check the wallet against it here and hard-block.
                // Sanctioned wallets are a more dangerous case than
                // sanctioned token accounts — the address looks
                // entirely valid until the transfer is broadcast.
                // See `claude-docs/intents/retail-yaas.md`.
                return {
                    text: "Valid account",
                    tone: "success",
                    accountType: "wallet",
                    invalid: false
                };
            }
            if (owner === TOKEN_PROGRAM_ID_STRING || owner === TOKEN_2022_PROGRAM_ID_STRING) {
                // Token Program owns it — but mints vs accounts
                // need different copy and (for mints / non-USDv
                // accounts) hard-block treatment, since USDv
                // rewards cannot transfer to either case.
                if (account?.tokenKind === "mint") {
                    return {
                        text: "Invalid account",
                        tone: "error",
                        accountType: "token",
                        invalid: true
                    };
                }
                if (account?.tokenKind === "account") {
                    // Frozen account — state byte at offset 108 is
                    // `2`. The freeze authority froze this account;
                    // transfers reject on chain until thawed.
                    // Hard-block regardless of mint match, because
                    // submit would fail anyway.
                    if (account.tokenAccountState === 2) {
                        return {
                            text: "Frozen account",
                            tone: "error",
                            accountType: "token",
                            invalid: true
                        };
                    }
                    // Uninitialized accounts (state byte `0`) fall
                    // through to the mint check below — bytes 0–32
                    // won't decode to USDv's mint when the account
                    // was never initialized, so they end up in the
                    // generic "Invalid account" branch by default.
                    // No dedicated path needed.
                    // TODO: Sanctioned-address check. When a
                    // sanctions-list integration lands (Circle's
                    // USDC blocklist / Chainalysis / OFAC SDN /
                    // Solomon-maintained list), check the
                    // destination against it here and hard-block.
                    // The check belongs in this branch so it runs
                    // after structural validity is established. See
                    // `claude-docs/intents/retail-yaas.md` for the
                    // tracking note.
                    if (isAllowedUsdvTokenAccount(account.tokenMint, owner, allowLegacyUsdvTokenAccount)) {
                        // USDv ATA is a fully valid destination —
                        // suppress status text so the badge alone
                        // tells the story. No nudge to "send to the
                        // wallet" because pasting the USDv ATA is
                        // legitimate (just unusual).
                        return {
                            text: null,
                            tone: "success",
                            accountType: "token",
                            invalid: false
                        };
                    }
                    return {
                        text: "Invalid account",
                        tone: "error",
                        accountType: "token",
                        invalid: true
                    };
                }
                // A Token Program-owned account with an unknown
                // layout cannot be validated as a transferable USDv
                // account. Do not let a destination that will fail
                // on chain pass client-side validation.
                return {
                    text: "Invalid account",
                    tone: "error",
                    accountType: "token",
                    invalid: true
                };
            }
            if (owner) {
                // Any non-System, non-Token owner OR System-owned
                // off-curve account. The badge tells the user the
                // category; the prompt copy invites them to
                // double-check rather than dumping the owner ID
                // they have to interpret.
                return {
                    text: "Is this address correct?",
                    tone: "warning",
                    accountType: "program",
                    invalid: false
                };
            }
            return {
                text: null,
                tone: "neutral",
                accountType: null,
                invalid: false
            };
        }
        case "failed": {
            // Auto-retry: first failure → 5s, second failure → 10s,
            // third → give up. The countdown ticks in the hook; if
            // we briefly hit countdown=0 between attempts, fall back
            // to the schedule's initial delay so the user never sees
            // "retrying in 0..." (status would be transitioning to
            // "checking" anyway, but the fallback avoids the flash).
            if (retryState.attempt >= RETRY_DELAYS_SEC.length) {
                return {
                    text: "Error verifying, please try again later.",
                    tone: "error",
                    accountType: null,
                    invalid: false
                };
            }
            const displaySeconds =
                retryState.countdown > 0 ? retryState.countdown : RETRY_DELAYS_SEC[retryState.attempt];
            return {
                text: `Error verifying, retrying in ${displaySeconds}...`,
                tone: "error",
                accountType: null,
                invalid: false
            };
        }
        case "idle":
            return {
                text: null,
                tone: "neutral",
                accountType: null,
                invalid: false
            };
    }
}
